import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { DurchfuehrungsTag } from '@chancenwerk/_cms/products/entities/Product.js';
import { ProductSCH } from '@chancenwerk/_cms/products/entities/ProductSCH.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import type { DateTime } from 'luxon';
import { Course } from './Course.js';

function sameHourMinute(a: Maybe<DateTime>, b: Maybe<DateTime>) {
  if (!a || !b) {
    return false;
  }
  return a.hasSame(b, 'hour') && a.hasSame(b, 'minute');
}

function createTimeChunks(dt: DurchfuehrungsTag[]) {
  const chunks: Record<string, DurchfuehrungsTag[]> = {};
  for (const day of dt) {
    chunks[day.cid ?? 'unknown'] ??= [];
    chunks[day.cid ?? 'unknown']?.push(day);
  }
  return chunks;
}

//
// Mutations that can happen:
//
// - Location change on product
// - A chunk of days is created
// - A chunk of days is deleted
// - The start time of a chunk is changed
// - N days are added to a chunk
// - N days are removed from a chunk
// - N days are added and removed from a chunk
//

/**
 * @entity CourseChancenschule
 */
@Entity()
export class CourseChancenschule extends Course {
  public static override _class = 'CourseChancenschule';

  public static async updateAndCreateCoursesOfKPF(kpf: KPF<ProductSCH>, oldKPF?: KPF<ProductSCH>) {
    // * Add list of changed courses
    const coursesToUpdate: CourseChancenschule[] = [];
    const coursesToRemove: CourseChancenschule[] = [];

    // * Get list of all courses from the kpf
    const courseCollection = await getRawCollection<EntityData<CourseChancenschule>>(
      CourseChancenschule._collectionName,
    );
    const courses: CourseChancenschule[] = (
      await courseCollection.find({ kpfID: kpf._id, _class: CourseChancenschule._class }).toArray()
    ).map(c => constructValue(c) as CourseChancenschule);

    if (!kpf.produkt || kpf.produkt?._class !== ProductSCH._class) return;
    if (!oldKPF?.produkt || oldKPF.produkt?._class !== ProductSCH._class) return;

    const curKpf = await loadMongoData(KPF<ProductSCH>, kpf._id);
    const timeChunks: Record<string, DurchfuehrungsTag[]> = createTimeChunks(kpf.produkt.durchfuehrungsTage);
    const oldTimeChunks: Record<string, DurchfuehrungsTag[]> = createTimeChunks(oldKPF.produkt.durchfuehrungsTage);

    // * Check for location change
    if (curKpf?.produkt.location) {
      for (const course of courses) {
        if (course.location !== curKpf?.produkt.location) {
          course.location = curKpf?.produkt.location;
          coursesToUpdate.push(course);
        }
      }
    }

    // * Check for course chunks that got deleted
    for (const [cid, oldDays] of Object.entries(oldTimeChunks)) {
      if (timeChunks[cid]) {
        continue;
      }

      const start = oldDays[0]?.startZeit;
      if (!start) {
        continue;
      }

      // * Remove all courses with the removed start time
      coursesToRemove.push(...courses.filter(c => sameHourMinute(c.startTime, start)));
    }

    // * Check for course chunks that got created
    for (const [cid, days] of Object.entries(timeChunks)) {
      const oldDays = oldTimeChunks[cid];
      if (oldDays) {
        continue;
      }

      for (const day of days) {
        coursesToUpdate.push(
          new CourseChancenschule({
            kpfID: kpf._id,
            startTime: day.startZeit,
            endTime: day.endZeit,
            weekday: day.wochentag,
            location: curKpf?.produkt.location,
          }),
        );
      }
    }

    // * Check for updates in starTime
    for (const [cid, days] of Object.entries(timeChunks)) {
      const oldDays = oldTimeChunks[cid];
      if (!oldDays) {
        continue;
      }

      const newStart = days[0]?.startZeit;
      if (!newStart) {
        continue;
      }

      const oldStart = oldDays[0]?.startZeit;
      if (!oldStart) {
        continue;
      }

      if (sameHourMinute(newStart, oldStart)) {
        continue;
      }

      // * Update all courses with the new start time
      coursesToUpdate.push(
        ...courses
          .filter(c => sameHourMinute(c.startTime, oldStart))
          .map(c => {
            c.startTime = newStart;
            return c;
          }),
      );
    }

    // * Check for changes in days
    for (const [cid, days] of Object.entries(timeChunks)) {
      const oldDays = oldTimeChunks[cid];
      if (!oldDays) {
        continue;
      }

      for (const day of days) {
        if (courses.find(c => c.weekday === day.wochentag && sameHourMinute(c.startTime, day.startZeit))) {
          continue;
        }

        // * This seems to be a new weekday, so we need to create a new course
        coursesToUpdate.push(
          new CourseChancenschule({
            kpfID: kpf._id,
            startTime: day.startZeit,
            endTime: day.endZeit,
            weekday: day.wochentag,
            location: curKpf?.produkt.location,
          }),
        );
      }

      // * Check for courses that don't have a associated weekday anymore. These need to be deleted.
      const weekdaysToDelete = oldDays.filter(
        old => !days.find(day => day.wochentag === old.wochentag && sameHourMinute(day.startZeit, old.startZeit)),
      );
      for (const old of weekdaysToDelete) {
        coursesToRemove.push(
          ...courses.filter(c => c.weekday === old.wochentag && sameHourMinute(c.startTime, old.startZeit)),
        );
      }
    }

    logger.info(
      `[ChancenSchule] Updating ${coursesToUpdate.length} courses and removing ${coursesToRemove.length} courses`,
    );

    // * Save courses
    for (const course of coursesToUpdate) {
      if (!course._id) await course.create();
      await course.save();
    }

    // * Remove courses
    for (const course of coursesToRemove) {
      await course.remove();
    }
  }
}
